import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api, ApiError } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import { MOCK_SESSION, USE_MOCKS } from '@/mocks/fixtures'
import type { CurrentUser } from '@/types/domain'

/* Sign-in (FR-57, epics Story 1.2).
 *
 * There is no token to keep. The server answers with the session as an opaque
 * httpOnly cookie (AD-21) and returns the user record as the response body, so
 * the only thing the client does with a successful sign-in is SEED the session
 * cache with what came back. Seeding rather than invalidating matters: the
 * guard redirects on `!user`, and a refetch round-trip would bounce the user
 * back to this page for the length of one request.
 *
 * `credentials: 'include'` is already the default in lib/api, which is what
 * lets the browser store the cookie the response sets. */

export interface Credentials {
  email: string
  password: string
}

/** Why a sign-in failed, reduced to the cases this form has copy for.
 *
 *  The server deliberately answers identically for a wrong password and an
 *  unknown email — same body, same timing (Story 1.2) — so `invalid` covers
 *  both and there is no branch here that could tell them apart. */
export type SignInFailure = 'invalid' | 'throttled' | 'server'

/** Maps a failure to the case the form renders. 429 is the rate limiter (five
 *  attempts per email in fifteen minutes); 401 is the generic refusal. */
export function signInFailure(error: unknown): SignInFailure {
  if (error instanceof ApiError) {
    if (error.status === 429) return 'throttled'
    if (error.isUnauthenticated || error.status === 400) return 'invalid'
  }
  return 'server'
}

export function useSignIn() {
  const queryClient = useQueryClient()

  return useMutation<CurrentUser, unknown, Credentials>({
    mutationFn: (credentials) =>
      USE_MOCKS
        ? Promise.resolve(MOCK_SESSION)
        : api.post<CurrentUser>('/session', credentials),

    onSuccess: (user) => {
      /* Anything cached under the previous identity must not survive a new
       * sign-in — a shared machine would otherwise show the last user's rows
       * until each query happened to refetch. Clear first, then seed, so the
       * session we just received is not swept away with everything else. */
      queryClient.clear()
      queryClient.setQueryData(queryKeys.session, user)
    },
  })
}
